// ============================================================================
// Restricted answers — the values only HR, executives and payroll may read.
//
// WHY THIS EXISTS. The company never prefills an SSN, date of birth or bank
// account, but the employee has to be able to type one in and send it back,
// or HR cannot start pay (TCSD-0079). Those answers are kept out of
// `esign_fields.value` by CHECK constraint and live in `esign_field_secrets`,
// gated on `can_view_restricted_hr()`. Until this panel existed there was
// nowhere in the app to READ them — the whole collection mechanism ended in a
// table nobody could open, which is not a safer system, just a broken one.
//
// THREE RULES THIS SCREEN KEEPS.
//  1. Nothing is shown until somebody asks for it. The panel is collapsed, and
//     inside it every value is masked until that row is individually revealed.
//     Shoulder-surfing a payroll screen is a real way for an SSN to leak.
//  2. It FAILS CLOSED and says so. The RPC raises for anyone outside the gate
//     rather than returning an empty list, and this screen prints the refusal.
//     "You may not see these" and "there are none" must never look the same.
//  3. It never writes, copies or logs a value. Reveal is local component state
//     and is dropped when the panel closes.
// ============================================================================

import { useEffect, useState } from 'react';
import {
  ChevronDown, Eye, EyeOff, Loader2, ShieldAlert, ShieldCheck,
} from 'lucide-react';
import { EsignFieldSecret, listEnvelopeSecrets } from './envelope-lib';

/**
 * A human label for a restricted answer. `esign_fields` carries no category —
 * `is_sensitive` is one boolean — so the honest thing is to name the field
 * type and its page rather than to guess "this one is the SSN".
 */
function labelFor(s: EsignFieldSecret): string {
  const kind = s.field_type === 'date' ? 'Date' : 'Text';
  return `${kind} answer · page ${s.page}`;
}

export function RestrictedAnswers({ envelopeId }: { envelopeId: string }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<EsignFieldSecret[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!open || rows || error) return;
    listEnvelopeSecrets(envelopeId)
      .then(setRows)
      .catch((e: Error) => setError(e.message));
  }, [open, rows, error, envelopeId]);

  // Closing the panel forgets every reveal. Re-opening starts masked again.
  useEffect(() => {
    if (!open) setRevealed(new Set());
  }, [open]);

  function toggleReveal(fieldId: string) {
    setRevealed((prev) => {
      const next = new Set(prev);
      if (next.has(fieldId)) next.delete(fieldId);
      else next.add(fieldId);
      return next;
    });
  }

  return (
    <section className="rounded-md border border-tcs-border bg-tcs-raised">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-3 py-3 text-left"
      >
        <ShieldCheck className="h-4 w-4 shrink-0 text-tcs-accent-ink" aria-hidden />
        <span className="flex-1 text-sm font-semibold text-tcs-text">
          Restricted answers
        </span>
        {rows && (
          <span className="text-xs text-tcs-text-muted">
            {rows.length === 0 ? 'none' : `${rows.length} returned`}
          </span>
        )}
        <ChevronDown
          className={`h-4 w-4 text-tcs-text-muted transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden
        />
      </button>

      {open && (
        <div className="border-t border-tcs-border p-3">
          <p className="mb-3 flex items-start gap-2 text-xs text-tcs-text-secondary">
            <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0 text-tcs-accent-ink" aria-hidden />
            <span>
              Only HR, executives and payroll can open this. The employee typed
              these in; we never filled them in for them, and they are not
              stored anywhere the rest of the company can read.
            </span>
          </p>

          {error && (
            <p role="alert" className="text-sm text-st-danger-fg">
              {error.includes('TCS_ESIGN_RESTRICTED') || error.includes('Restricted to')
                ? 'You do not have access to these answers.'
                : error}
            </p>
          )}

          {!rows && !error && (
            <p className="flex items-center gap-2 text-sm text-tcs-text-muted">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading…
            </p>
          )}

          {rows?.length === 0 && (
            <p className="text-sm text-tcs-text-muted">
              This envelope has no restricted answers — nothing on it asked for
              an SSN, date of birth or bank details.
            </p>
          )}

          {rows && rows.length > 0 && (
            <ul className="space-y-2">
              {rows.map((s) => {
                const shown = revealed.has(s.field_id);
                return (
                  <li
                    key={s.field_id}
                    className="flex items-center gap-3 rounded border border-tcs-border bg-tcs-bg px-3 py-2"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-tcs-text-muted">{labelFor(s)}</p>
                      <p className="font-mono text-sm text-tcs-text">
                        {shown ? s.value : '••••••••••'}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => toggleReveal(s.field_id)}
                      aria-pressed={shown}
                      className="flex shrink-0 items-center gap-1 rounded border border-tcs-border px-2 py-1 text-xs text-tcs-text-secondary"
                    >
                      {shown ? (
                        <>
                          <EyeOff className="h-3.5 w-3.5" aria-hidden /> Hide
                        </>
                      ) : (
                        <>
                          <Eye className="h-3.5 w-3.5" aria-hidden /> Show
                        </>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
